import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { projectsApi, vendorsApi, type Project, type VendorResponse as ApiVendorResponse } from '../lib/api'
import { Button } from '../components/ui/Button'
import { StatusBadge } from '../components/ui/Badge'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { RequirementsTab } from '../components/RequirementsTab'
import { ResponsesTab } from '../components/ResponsesTab'
import { ProjectSettingsModal } from '../components/modals/ProjectSettingsModal'
import { KebabMenu } from '../components/ui/KebabMenu'
import { trackVendorResponsesUploaded, trackRfpProjectClosed } from '@/analytics/events'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  Settings,
  Calendar,
  FileText,
  Users,
  Hash,
  X,
  Trash2
} from 'lucide-react'
import { formatDate } from '../lib/utils'

export const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'requirements' | 'responses'>(
    searchParams.get('tab') === 'responses' ? 'responses' : 'requirements'
  )
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [justCompleted, setJustCompleted] = useState(false)
  const completionTimeRef = useRef<number | null>(null)

  // Pagination state for vendor responses
  const [responsesPage, setResponsesPage] = useState(1)
  const [responsesPageSize, setResponsesPageSize] = useState(20)

  // Fetch project data
  const { data: project, isLoading, refetch } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId!),
    {
      enabled: !!projectId,
      refetchInterval: (data) => {
        // Auto-refresh while analyzing
        if (data?.extraction_status === 'in_progress') {
          return 3000 // Poll every 3 seconds during extraction
        }
        
        // Continue polling for 15 seconds after completion to ensure requirements data is loaded
        if (justCompleted && completionTimeRef.current) {
          const timeSinceCompletion = Date.now() - completionTimeRef.current
          if (timeSinceCompletion < 15000) { // 15 seconds
            return 2000 // Poll every 2 seconds after completion
          } else {
            setJustCompleted(false) // Stop the additional polling
            completionTimeRef.current = null
          }
        }
        
        return false // No polling by default
      }
    }
  )

  // Fetch vendor responses separately with pagination
  const { data: vendorResponses, refetch: refetchVendors } = useQuery(
    ['vendorResponses', projectId, responsesPage, responsesPageSize],
    () => vendorsApi.list(projectId!, {
      skip: (responsesPage - 1) * responsesPageSize,
      limit: responsesPageSize
    }),
    {
      enabled: !!projectId,
      refetchInterval: (data) => {
        // Check if any vendor responses are being analyzed or have status 'pending'
        const processingResponses = data?.data?.filter(response =>
          response.status === 'pending' ||
          response.overall_requirement_status === 'pending' ||
          (response.files && response.files.some(file => file.status === 'uploading'))
        ) || []

        if (processingResponses.length > 0) {
          console.log(`🔄 Polling vendor responses: ${processingResponses.length} responses being processed`)
          // Poll more frequently during bulk uploads
          return processingResponses.some(r => r.status === 'pending') ? 1000 : 3000
        }

        return false // No polling when all responses are processed
      }
    }
  )

  // Track extraction status changes to trigger extended polling after completion
  useEffect(() => {
    if (project?.extraction_status === 'completed' && !justCompleted) {
      console.log('🎉 Requirements extraction completed, starting extended polling for requirements data')
      setJustCompleted(true)
      completionTimeRef.current = Date.now()
    }
  }, [project?.extraction_status, justCompleted])


  // Handle upload multiple vendor responses (folder-based) with async processing
  const handleUploadVendorResponses = async (vendors: Array<{vendorName: string, files: File[]}>) => {
    if (!projectId) return
    
    // Prepare data for bulk upload - send ALL files for each vendor
    const formData = new FormData()
    const vendorNames: string[] = []
    const vendorEmails: string[] = []
    const vendorPhones: string[] = []
    const vendorAddresses: string[] = []
    const notesList: string[] = []
    const fileVendorMapping: Record<string, string> = {} // Map file index to vendor name
    
    let fileIndex = 0
    vendors.forEach((vendor) => {
      if (vendor.files.length > 0) {
        // Add ALL files for each vendor
        vendor.files.forEach((file) => {
          formData.append('files', file)
          fileVendorMapping[fileIndex.toString()] = vendor.vendorName
          fileIndex++
        })
        
        // Add vendor info once per vendor (not per file)
        vendorNames.push(vendor.vendorName)
        vendorEmails.push('') // Will be extracted from document if available
        vendorPhones.push('')
        vendorAddresses.push('')
        notesList.push(`Uploaded from folder: ${vendor.vendorName} (${vendor.files.length} files)`)
      }
    })
    
    // Add vendor information
    vendorNames.forEach(name => {
      formData.append('vendor_names', name);
    })
    formData.append('vendor_emails', vendorEmails.join(','))
    formData.append('vendor_phones', vendorPhones.join(','))
    formData.append('vendor_addresses', vendorAddresses.join(','))
    formData.append('notes_list', JSON.stringify(notesList))
    formData.append('file_vendor_mapping', JSON.stringify(fileVendorMapping))
    
    try {
      // Use the new async bulk upload endpoint
      const response = await vendorsApi.bulkUploadAsync(projectId, formData)
      trackVendorResponsesUploaded(projectId, {
        total_vendors: response.total_vendors,
        total_files: response.total_files,
      })

      // Show immediate success message
      toast.success(`Started processing ${response.total_vendors} vendor responses (${response.total_files} total files).`)
      
      // Start polling for progress
      const checkProgress = async () => {
        try {
          const progress = await vendorsApi.getBulkUploadProgress(response.bulk_task_id)
          
          if (progress.status === 'completed') {
            toast.success(`All ${progress.total_vendors} vendor responses processed successfully!`)
            refetch() // Refresh to show new responses and evaluation status
            refetchVendors() // Refresh vendor responses list
            return
          } else if (progress.status === 'failed') {
            toast.error('Some vendor responses failed to process. Check individual vendor status for details.')
            refetch()
            refetchVendors()
            return
          }
          
          // Continue polling if still processing
          if (progress.processing_vendors > 0 || progress.status === 'processing') {
            setTimeout(checkProgress, 2000) // Check every 2 seconds
          } else {
            // All done
            refetch()
            refetchVendors()
          }
        } catch (progressError) {
          console.error('Error checking upload progress:', progressError)
          // Fallback: just refresh the data
          setTimeout(() => {
            refetch()
            refetchVendors()
          }, 5000)
        }
      }
      
      // Start checking progress after 1 second
      setTimeout(checkProgress, 1000)
      
    } catch (error) {
      console.error('Bulk upload error:', error)
      toast.error('Failed to start vendor response upload')
      throw error
    }
  }

  // Handle project settings update
  const handleUpdateProject = async (data: { title: string; due_date: string }) => {
    if (!projectId) return
    
    try {
      await projectsApi.update(projectId, data)
      toast.success('Project updated successfully')
      refetch() // Refresh project data
      queryClient.invalidateQueries(['projects']) // Update sidebar
    } catch (error) {
      toast.error('Failed to update project')
      throw error
    }
  }

  // Handle project close
  const handleCloseProject = async () => {
    if (!projectId) return
    
    if (!window.confirm('Are you sure you want to close this project? This will set the project status to closed and prevent further modifications.')) {
      return
    }
    
    try {
      await projectsApi.update(projectId, { status: 'closed' })
      trackRfpProjectClosed(projectId)
      toast.success('Project closed successfully')
      refetch() // Refresh project data
      queryClient.invalidateQueries(['projects']) // Update sidebar
    } catch (error) {
      toast.error('Failed to close project')
      console.error('Error closing project:', error)
    }
  }

  // Handle project delete (available once the project is closed)
  const handleDeleteProject = async () => {
    if (!projectId) return

    if (!window.confirm(`Are you sure you want to delete the project "${project?.title}"? This will permanently delete the project and all its vendor responses, evaluations, and related data. This action cannot be undone.`)) {
      return
    }

    setIsDeleting(true)
    try {
      await projectsApi.delete(projectId)
      toast.success('Project deleted successfully')
      queryClient.invalidateQueries(['projects']) // Update sidebar/dashboard
      navigate('/') // Project no longer exists — return to the dashboard
    } catch (error) {
      toast.error('Failed to delete project')
      console.error('Error deleting project:', error)
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Project not found</h2>
          <p className="text-gray-600 mt-2">The project you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/projects')} className="mt-4">
            Back to Projects
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Header — original layout on a light banner card, DS canonical-banner
          style (sop-studio inspired): white surface, soft purple glow, faint
          ink grid. */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <section className="relative max-w-7xl mx-auto overflow-hidden rounded-3xl bg-white border border-gray-200 shadow-sm px-6 py-6 sm:px-8 sm:py-7">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(60% 90% at 92% 8%, rgba(140,90,210,0.10) 0%, transparent 55%), radial-gradient(50% 70% at 4% 115%, rgba(140,90,210,0.06) 0%, transparent 60%)' }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: 'linear-gradient(to right, rgba(27,27,31,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(27,27,31,0.04) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
              maskImage: 'radial-gradient(ellipse at 25% 35%, #000 0%, transparent 75%)',
              WebkitMaskImage: 'radial-gradient(ellipse at 25% 35%, #000 0%, transparent 75%)',
            }}
          />

          <div className="relative flex items-center justify-between gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="p-2 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>

              <div className="min-w-0">
                <h1 className="font-title text-3xl font-bold text-gray-900 tracking-tight">{project.title}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <StatusBadge status={project.status} />

                  {project.project_number && (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Hash className="w-4 h-4" />
                      Bid: {project.project_number}
                    </div>
                  )}

                  {project.due_date && (
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      Due {formatDate(project.due_date)}
                    </div>
                  )}

                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <FileText className="w-4 h-4" />
                    {project.requirements?.length || 0} requirements
                  </div>

                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    {vendorResponses?.data?.length || 0} responses
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {project.status !== 'closed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCloseProject}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Close Project
                </Button>
              )}
              <KebabMenu
                triggerClassName="p-1.5 rounded-sm border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                items={[
                  {
                    label: 'Settings',
                    icon: <Settings className="w-4 h-4" />,
                    onSelect: () => setShowSettingsModal(true),
                  },
                  {
                    label: isDeleting ? 'Deleting...' : 'Delete Project',
                    icon: <Trash2 className="w-4 h-4" />,
                    onSelect: handleDeleteProject,
                    destructive: true,
                    disabled: isDeleting,
                  },
                ]}
              />
            </div>
          </div>
        </section>
      </div>

      {/* Tabs — DS underline tab (same as taskline drawer tabs): bottom border,
          active in primary ink. */}
      <div className="flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('requirements')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'requirements'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}
            >
              Requirements
              {project.extraction_status === 'in_progress' && (
                <span className="inline-block w-2 h-2 bg-current rounded-full animate-pulse opacity-70"></span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('responses')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'responses'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}
            >
              Responses
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-full overflow-y-auto">
        {activeTab === 'requirements' && (
          <RequirementsTab 
            project={project}
            onUpdateRequirement={async (requirement) => {
              try {
                await projectsApi.updateRequirement(projectId!, requirement.requirement_id, requirement)
                toast.success('Requirement updated successfully')
                refetch()
              } catch (error) {
                toast.error('Failed to update requirement')
              }
            }}
            onDeleteRequirement={async (requirementId) => {
              if (window.confirm('Are you sure you want to delete this requirement?')) {
                try {
                  await projectsApi.deleteRequirement(projectId!, requirementId)
                  toast.success('Requirement deleted successfully')
                  refetch()
                } catch (error) {
                  toast.error('Failed to delete requirement')
                }
              }
            }}
            onAddRequirement={async (requirementData) => {
              try {
                await projectsApi.addRequirement(projectId!, requirementData)
                toast.success('Requirement added successfully')
                refetch()
              } catch (error) {
                toast.error('Failed to add requirement')
              }
            }}
          />
        )}
        
        {activeTab === 'responses' && (
          <ResponsesTab
            project={{
              ...project,
              vendor_responses: vendorResponses?.data || [],
              extraction_status: project.extraction_status,
              extraction_error: project.extraction_error,
              requirements: project.requirements
            }}
            pagination={{
              currentPage: responsesPage,
              pageSize: responsesPageSize,
              totalCount: vendorResponses?.total_count || 0,
              totalPages: vendorResponses?.total_pages || 0,
              onPageChange: setResponsesPage,
              onPageSizeChange: (newSize) => {
                setResponsesPageSize(newSize)
                setResponsesPage(1) // Reset to first page when changing page size
              }
            }}
            onUploadVendorResponses={handleUploadVendorResponses}
            onAddResponse={() => {
              // TODO: Implement manual add response
              console.log('Add response manually')
            }}
            onRefresh={() => {
              refetch()
              refetchVendors()
            }}
          />
        )}
        </div>
      </div>

      {/* Project Settings Modal */}
      {project && (
        <ProjectSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          onSave={handleUpdateProject}
          project={project}
        />
      )}
    </>
  )
}
